import { useState, useEffect, useMemo, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/features/auth/AuthContext"
import { useAdminAccess } from "@/features/admin/useAdminAccess"
import { useBodyScrollLock } from "@/shared/lib/hooks/useBodyScrollLock"
import { useFocusTrap } from "@/shared/lib/hooks/useFocusTrap"
import { isNavItemActive } from "./navActiveMatchers"
import { NavbarBrand } from "./NavbarBrand"
import { NavbarPartners } from "./NavbarPartners"
import { NavbarDesktopLinks } from "./NavbarDesktopLinks"
import { NavbarAuthArea } from "./NavbarAuthArea"
import { NavbarMobileMenu } from "./NavbarMobileMenu"

export default function Navbar() {
  const { t } = useTranslation()
  const { isAuthenticated, logout } = useAuth()
  const { hasAnyAdminAccess } = useAdminAccess()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const wasMenuOpenRef = useRef(false)
  const menuToggleButtonRef = useRef<HTMLButtonElement | null>(null)
  const menuContainerRef = useRef<HTMLDivElement | null>(null)
  const location = useLocation()
  const isHome = location.pathname === "/"
  const showMainNav = !isHome
  const showMobileNavShell = !isHome || isAuthenticated

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  useBodyScrollLock(menuOpen)

  useEffect(() => {
    if (!menuOpen && wasMenuOpenRef.current) {
      menuToggleButtonRef.current?.focus()
    }
    wasMenuOpenRef.current = menuOpen
  }, [menuOpen])

  useFocusTrap(menuOpen, menuContainerRef)

  useEffect(() => {
    if (!menuOpen) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        setMenuOpen(false)
        menuToggleButtonRef.current?.focus()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [menuOpen])

  const isTransparent = isHome && !scrolled
  const headerTone = isTransparent ? "transparent" : "solid"
  const brandMarkLabel = `${t("brand.leninka")} ${t("brand.artIp")}`

  const handleToggleMenu = () => setMenuOpen((open) => !open)
  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleToggleMenu()
    }
  }

  const activeMap = useMemo(
    () => ({
      profile: isNavItemActive("profile", location.pathname),
      events: isNavItemActive("events", location.pathname),
      projects: isNavItemActive("projects", location.pathname),
      deposit: isNavItemActive("deposit", location.pathname),
      admin: isNavItemActive("admin", location.pathname),
      about: isNavItemActive("about", location.pathname),
    }),
    [location.pathname],
  )

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? "bg-transparent lg:bg-gradient-to-b lg:from-black/60 lg:to-transparent"
          : "bg-white border-b border-gray-100"
      }`}
    >
      <div
        className={`max-w-[1400px] mx-auto px-4 sm:px-6 flex flex-wrap xl:flex-nowrap items-center py-2 md:py-2.5 gap-2 sm:gap-3 w-full ${
          !showMainNav
            ? "max-sm:flex-col max-sm:items-stretch max-sm:gap-1.5 min-h-[4.5rem] sm:min-h-16 md:min-h-20 sm:justify-between"
            : "min-h-16 md:min-h-20"
        }`}
      >
        <div
          className={`flex flex-row gap-2 items-center min-w-0 sm:gap-3 md:gap-4 ${
            showMainNav
              ? "shrink-0"
              : "max-sm:order-2 max-sm:w-full max-sm:justify-center sm:min-w-0 sm:flex-1 sm:overflow-hidden sm:pr-2 md:pr-3"
          }`}
        >
          <NavbarBrand transparent={isTransparent} />
          <NavbarPartners onDarkBg={isTransparent} compact={!showMainNav} />
        </div>

        {showMainNav ? (
          <NavbarDesktopLinks
            activeMap={activeMap}
            headerTone={headerTone}
            isAuthenticated={isAuthenticated}
            hasAnyAdminAccess={hasAnyAdminAccess}
            ariaLabel={brandMarkLabel}
          />
        ) : (
          <div className="hidden xl:flex flex-1 min-w-0" aria-hidden />
        )}

        <div
          className={`flex items-center gap-2 sm:gap-3 shrink-0 min-w-0 ${
            !showMainNav
              ? "max-sm:order-1 max-sm:w-full max-sm:justify-end max-sm:ml-0 sm:ml-auto sm:order-2"
              : "order-2 ml-auto"
          }`}
        >
          <NavbarAuthArea
            isAuthenticated={isAuthenticated}
            headerTone={headerTone}
            onLogout={logout}
          />

          {showMobileNavShell ? (
            <button
              ref={menuToggleButtonRef}
              type="button"
              className={`md:hidden w-8 h-8 flex items-center justify-center rounded-md ${isTransparent ? "text-white" : "text-gray-700"}`}
              onClick={handleToggleMenu}
              onKeyDown={handleMenuKeyDown}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-menu"
              aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              tabIndex={0}
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
            </button>
          ) : null}
        </div>
      </div>

      {showMobileNavShell && menuOpen ? (
        <NavbarMobileMenu
          ref={menuContainerRef}
          id="mobile-nav-menu"
          ariaLabel={brandMarkLabel}
          isAuthenticated={isAuthenticated}
          hasAnyAdminAccess={hasAnyAdminAccess}
          activeMap={activeMap}
          onItemClick={() => setMenuOpen(false)}
          onLogout={logout}
        />
      ) : null}
    </header>
  )
}
